import React from 'react';

interface QualityScoreMeterProps {
  /** Score between 0 and 100. null renders a dash. */
  score: number | null;
  /** Render as a compact badge (default) or a wider bar. */
  variant?: 'badge' | 'bar';
  /** Additional classes on the outer element. */
  className?: string;
}

function scoreColor(score: number): string {
  if (score < 30) return 'text-red-600 dark:text-red-400';
  if (score < 70) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}

function scoreBg(score: number): string {
  if (score < 30) return 'bg-red-500';
  if (score < 70) return 'bg-yellow-500';
  return 'bg-green-500';
}

function scoreBadgeBg(score: number): string {
  if (score < 30) return 'bg-red-100 dark:bg-red-900/40';
  if (score < 70) return 'bg-yellow-100 dark:bg-yellow-900/40';
  return 'bg-green-100 dark:bg-green-900/40';
}

/**
 * Visual quality score indicator.
 *
 * - **badge**: a small rounded pill showing the numeric score, colour-coded.
 * - **bar**: a horizontal progress bar with the score label.
 */
export const QualityScoreMeter = React.memo(function QualityScoreMeter({
  score,
  variant = 'badge',
  className = '',
}: QualityScoreMeterProps) {
  if (score === null || score === undefined) {
    return (
      <span className={`text-xs text-gray-400 ${className}`}>--</span>
    );
  }

  const rounded = Math.round(score);

  if (variant === 'badge') {
    return (
      <span
        className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${scoreBadgeBg(rounded)} ${scoreColor(rounded)} ${className}`}
      >
        {rounded}
      </span>
    );
  }

  // bar variant
  return (
    <div className={`w-full ${className}`}>
      <div className="mb-0.5 flex items-center justify-between">
        <span className={`text-xs font-medium ${scoreColor(rounded)}`}>
          {rounded}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className={`h-full rounded-full transition-all ${scoreBg(rounded)}`}
          style={{ width: `${Math.min(rounded, 100)}%` }}
        />
      </div>
    </div>
  );
});
